import { useRef, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import { extractApiErrorMessage } from "../../utils/apiErrorMessage";

const formatUploadDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const base64Content = result.split(",")[1] || "";
      resolve(base64Content);
    };

    reader.onerror = () => {
      reject(new Error("Le fichier n'a pas pu etre lu."));
    };

    reader.readAsDataURL(file);
  });
};

const parseDownloadFilename = (headers, fallbackName) => {
  const disposition = headers?.["content-disposition"] || "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const basicMatch = disposition.match(/filename="?([^"]+)"?/i);

  if (basicMatch?.[1]) {
    return basicMatch[1];
  }

  return fallbackName;
};

const copyByFallback = async (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

function ConventionActions(props) {
  const {
    eleve,
    canUpload = false,
    canDownload = false,
    canDelete = false,
    onConventionUpdated,
    compact = false,
    stopPropagation = false,
  } = props;

  const fileInputRef = useRef(null);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!eleve) {
    return null;
  }

  const hasConvention = Boolean(eleve.conventionFinalePath);
  const canRenderActions = canUpload || canDownload || hasConvention;

  if (!canRenderActions) {
    return null;
  }

  const handleInternalClick = (event) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  const handleOpenFilePicker = (event) => {
    handleInternalClick(event);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    handleInternalClick(event);
    setFeedbackMessage("");
    setErrorMessage("");
    setUploading(true);

    try {
      const contentBase64 = await readFileAsBase64(file);
      const response = await axiosInstance.post(`/eleves/${eleve.id}/convention`, {
        fileName: file.name,
        mimeType: file.type,
        contentBase64,
      });

      setFeedbackMessage(
        response.data?.message ||
          "La convention finale a bien ete deposee pour cet eleve."
      );

      if (onConventionUpdated) {
        onConventionUpdated(response.data?.eleve);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(
        extractApiErrorMessage(
          error,
          "La convention finale n'a pas pu etre deposee."
        )
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (event) => {
    handleInternalClick(event);
    setFeedbackMessage("");
    setErrorMessage("");
    setDownloading(true);

    try {
      const response = await axiosInstance.get(`/eleves/${eleve.id}/convention`, {
        responseType: "blob",
      });

      const fileName = parseDownloadFilename(
        response.headers,
        eleve.conventionFinaleOriginalName || `convention-eleve-${eleve.id}.pdf`
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      setFeedbackMessage("La convention finale a ete telechargee.");
    } catch (error) {
      console.error(error);
      setErrorMessage(
        extractApiErrorMessage(
          error,
          "La convention finale n'a pas pu etre telechargee."
        )
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async (event) => {
    handleInternalClick(event);

    const shouldContinue = window.confirm(
      "Voulez-vous vraiment supprimer la convention finale de cet eleve ?"
    );

    if (!shouldContinue) {
      return;
    }

    setFeedbackMessage("");
    setErrorMessage("");
    setDeleting(true);

    try {
      const response = await axiosInstance.delete(`/eleves/${eleve.id}/convention`);

      setFeedbackMessage(
        response.data?.message ||
          "La convention finale a bien ete supprimee."
      );

      if (onConventionUpdated) {
        onConventionUpdated(response.data?.eleve);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(
        extractApiErrorMessage(
          error,
          "La convention finale n'a pas pu etre supprimee."
        )
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className={`convention-actions ${compact ? "is-compact" : ""}`}
      onClick={handleInternalClick}
    >
      {hasConvention && (
        <p className="convention-meta">
          Convention disponible :{" "}
          <strong>
            {eleve.conventionFinaleOriginalName || "Fichier depose"}
          </strong>
          {formatUploadDate(eleve.conventionFinaleUploadedAt) &&
            `, deposee le ${formatUploadDate(eleve.conventionFinaleUploadedAt)}`}
          {eleve.conventionFinaleUploadedByRole &&
            ` par ${eleve.conventionFinaleUploadedByRole.toLowerCase()}`}
          .
        </p>
      )}

      <div className="convention-actions-row">
        {canUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              hidden
              onChange={handleFileChange}
            />
            <button
              className="btn btn-secondary"
              type="button"
              disabled={uploading}
              onClick={handleOpenFilePicker}
            >
              {uploading
                ? "Depot en cours..."
                : hasConvention
                  ? "Remplacer la convention"
                  : "Deposer la convention"}
            </button>
          </>
        )}

        {canDownload && hasConvention && (
          <button
            className="btn"
            type="button"
            disabled={downloading}
            onClick={handleDownload}
          >
            {downloading
              ? "Telechargement..."
              : "Telecharger la convention"}
          </button>
        )}

        {canDelete && hasConvention && (
          <button
            className="btn btn-secondary"
            type="button"
            disabled={deleting}
            onClick={handleDelete}
          >
            {deleting
              ? "Suppression..."
              : "Supprimer la convention"}
          </button>
        )}
      </div>

      {feedbackMessage && (
        <p className="internal-feedback is-success">{feedbackMessage}</p>
      )}

      {errorMessage && (
        <p className="internal-feedback is-error">{errorMessage}</p>
      )}
    </div>
  );
}

export { copyByFallback };
export default ConventionActions;
